import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FileText, 
  Users, 
  Eye, 
  Download, 
  Filter,
  Search,
  Calendar,
  BookOpen,
  Plus,
  ExternalLink,
  TrendingUp,
  CheckCircle,
  Clock,
  X,
  BarChart3,
  Target
} from 'lucide-react';
import api from '../api';
import Pagination from '../common/Pagination';
import SearchAndFilters from './SearchAndFilters';
import ClasseCard from './ClasseCard';

const BulletinsTab = () => {
  const navigate = useNavigate();
  const [bulletins, setBulletins] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedClasse, setSelectedClasse] = useState('');
  const [selectedNiveau, setSelectedNiveau] = useState('');
  const [selectedAnnee, setSelectedAnnee] = useState('');
  const [selectedSemestre, setSelectedSemestre] = useState('');
  const [annees, setAnnees] = useState([]);
  const [niveaux, setNiveaux] = useState([]);
  
  // Nouveaux états pour la recherche et pagination
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(12);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Récupérer les années académiques
      const anneesResponse = await api.get('/etablissement/annees-academiques/');
      setAnnees(anneesResponse.data);
      
      // Récupérer l'année active par défaut
      const anneeActive = anneesResponse.data.find(a => a.status) || anneesResponse.data[0];
      if (anneeActive) {
        setSelectedAnnee(anneeActive.id);
        await fetchBulletinsByAnnee(anneeActive.id);
      }
      
    } catch (err) {
      setError('Erreur lors du chargement des données');
      console.error('Erreur fetchData:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchBulletinsByAnnee = async (anneeId) => {
    try {
      const response = await api.get(`/note/bulletins/?annee_academique_id=${anneeId}`);
      setBulletins(response.data.data || response.data);
      
      // Extraire les classes et niveaux uniques
      const bulletinsData = response.data.data || response.data || [];
      const classesUniques = bulletinsData
        .filter(b => b.classe && b.classe.id)
        .map(b => b.classe)
        .filter((classe, index, self) => 
          index === self.findIndex(c => c.id === classe.id)
        );
      
      const niveauxUniques = bulletinsData
        .filter(b => b.classe?.niveau && b.classe.niveau.id)
        .map(b => b.classe.niveau)
        .filter((niveau, index, self) => 
          index === self.findIndex(n => n.id === niveau.id)
        );
      
      setClasses(classesUniques);
      setNiveaux(niveauxUniques);
      
    } catch (err) {
      console.error('Erreur fetchBulletinsByAnnee:', err);
    }
  };

  const handleAnneeChange = async (anneeId) => {
    setSelectedAnnee(anneeId);
    setCurrentPage(1); // Reset à la première page
    await fetchBulletinsByAnnee(anneeId);
  };

  const handleVoirBulletin = (bulletinId) => {
    navigate(`/bulletin/${bulletinId}`);
  };

  const handleVoirTous = (classeId) => {
    // Navigation vers la page listant tous les bulletins de cette classe
    const params = new URLSearchParams({
      classe_id: classeId
    });
    
    // Ajouter les filtres actuels s'ils sont définis
    if (selectedAnnee) params.append('annee_academique_id', selectedAnnee);
    if (selectedSemestre) params.append('semestre_id', selectedSemestre);
    
    navigate(`/bulletins-classe?${params.toString()}`);
  };

  const handleDownload = (classeId) => {
    // Logique pour télécharger tous les bulletins d'une classe
    console.log('Téléchargement des bulletins pour la classe:', classeId);
  };

  const clearFilters = () => {
    setSelectedClasse('');
    setSelectedNiveau('');
    setSearchTerm('');
    setCurrentPage(1);
  };

  const handleFiltersChange = () => {
    setCurrentPage(1); // Reset à la première page lors du changement de filtres
  };

  // Filtrage et pagination avec useMemo pour optimiser les performances
  const { filteredBulletins, groupedByClasse, paginatedClasses } = useMemo(() => {
    // Filtrage des bulletins
    let filtered = bulletins.filter(bulletin => {
      if (selectedClasse && bulletin.classe?.id !== parseInt(selectedClasse)) return false;
      if (selectedNiveau && bulletin.classe?.niveau?.id !== parseInt(selectedNiveau)) return false;
      return true;
    });

    // Filtrage par recherche de classe
    if (searchTerm) {
      filtered = filtered.filter(bulletin => 
        bulletin.classe?.libelle?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        bulletin.classe?.niveau?.libelle?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        bulletin.etudiant?.nom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        bulletin.etudiant?.prenom?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Groupement par classe
    const grouped = filtered.reduce((acc, bulletin) => {
      const classeId = bulletin.classe?.id;
      
      if (!classeId || !bulletin.classe) {
        return acc;
      }
      
      if (!acc[classeId]) {
        acc[classeId] = {
          classe: bulletin.classe,
          bulletins: [],
          total: 0,
          valides: 0,
          en_attente: 0
        };
      }
      acc[classeId].bulletins.push(bulletin);
      acc[classeId].total++;
      if (bulletin.est_valide) {
        acc[classeId].valides++;
      } else {
        acc[classeId].en_attente++;
      }
      return acc;
    }, {});

    // Pagination des classes
    const classesArray = Object.values(grouped);
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginated = classesArray.slice(startIndex, endIndex);

    return {
      filteredBulletins: filtered,
      groupedByClasse: grouped,
      paginatedClasses: paginated
    };
  }, [bulletins, selectedClasse, selectedNiveau, searchTerm, currentPage, pageSize]);

  // Calcul des statistiques
  const stats = useMemo(() => {
    const totalBulletins = filteredBulletins.length;
    const bulletinsValides = filteredBulletins.filter(b => b.est_valide).length;
    const totalClasses = Object.keys(groupedByClasse).length;
    const tauxValidation = totalBulletins > 0 ? Math.round((bulletinsValides / totalBulletins) * 100) : 0;

    // Calculer la moyenne générale des bulletins
    let moyenneGenerale = 0;
    if (totalBulletins > 0) {
      const totalPoints = filteredBulletins.reduce((sum, b) => {
        return sum + (b.moyenne_semestre?.moyenne_generale || 0);
      }, 0);
      moyenneGenerale = Math.round((totalPoints / totalBulletins) * 100) / 100;
    }

    return {
      totalBulletins,
      bulletinsValides,
      totalClasses,
      tauxValidation,
      moyenneGenerale
    };
  }, [filteredBulletins, groupedByClasse]);

  // Pagination info
  const paginationInfo = useMemo(() => {
    const totalClasses = Object.keys(groupedByClasse).length;
    const totalPages = Math.ceil(totalClasses / pageSize);
    
    return {
      current_page: currentPage,
      total_pages: totalPages,
      count: totalClasses,
      page_size: pageSize,
      has_next: currentPage < totalPages,
      has_previous: currentPage > 1
    };
  }, [groupedByClasse, currentPage, pageSize]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#202654]"></div>
        <span className="ml-3 text-gray-600">Chargement des bulletins...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <div className="text-red-500 mb-4">
          <FileText className="h-12 w-12 mx-auto mb-2" />
          <p className="text-lg font-medium">{error}</p>
        </div>
        <button
          onClick={fetchData}
          className="px-4 py-2 bg-[#202654] text-white rounded-lg hover:bg-[#202654]/90 transition-colors"
        >
          Réessayer
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* En-tête avec statistiques principales */}
      <div className="bg-gradient-to-r from-[#202654] to-[#374151] rounded-xl shadow-lg text-white p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold">Gestion des Bulletins</h1>
            <p className="text-blue-100">Suivi et gestion des bulletins scolaires</p>
          </div>
          <button
            onClick={() => navigate('/create-bulletin')}
            className="flex items-center px-6 py-3 bg-white text-[#202654] rounded-lg hover:bg-gray-100 transition-all duration-200 shadow-lg hover:shadow-xl font-medium"
          >
            <Plus className="h-5 w-5 mr-2" />
            Générer des Bulletins
          </button>
        </div>
        
        {/* Statistiques en-tête */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center">
            <div className="text-3xl font-bold">{stats.totalBulletins}</div>
            <div className="text-blue-100 text-sm">Total Bulletins</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold">{stats.totalClasses}</div>
            <div className="text-blue-100 text-sm">Classes</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold">{stats.bulletinsValides}</div>
            <div className="text-blue-100 text-sm">Validés</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold">{stats.tauxValidation}%</div>
            <div className="text-blue-100 text-sm">Taux Validation</div>
          </div>
        </div>
      </div>

      {/* Composant de recherche et filtres */}
      <SearchAndFilters
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        selectedAnnee={selectedAnnee}
        setSelectedAnnee={setSelectedAnnee}
        selectedNiveau={selectedNiveau}
        setSelectedNiveau={setSelectedNiveau}
        selectedClasse={selectedClasse}
        setSelectedClasse={setSelectedClasse}
        annees={annees}
        niveaux={niveaux}
        classes={classes}
        onClearFilters={clearFilters}
        onFiltersChange={handleFiltersChange}
      />

      {/* Statistiques détaillées */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Bulletins</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalBulletins}</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg">
              <FileText className="h-6 w-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Classes</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalClasses}</p>
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <Users className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Validés</p>
              <p className="text-2xl font-bold text-green-600">{stats.bulletinsValides}</p>
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Moyenne Générale</p>
              <p className="text-2xl font-bold text-purple-600">{stats.moyenneGenerale}/20</p>
            </div>
            <div className="p-3 bg-purple-100 rounded-lg">
              <Target className="h-6 w-6 text-purple-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Liste des bulletins par classe */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">Bulletins par Classe</h3>
            <div className="flex items-center space-x-2 text-sm text-gray-500">
              <BookOpen className="h-4 w-4" />
              <span>{stats.totalClasses} classes trouvées</span>
              {searchTerm && (
                <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">
                  Recherche: "{searchTerm}"
                </span>
              )}
            </div>
          </div>
        </div>

        {paginatedClasses.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun bulletin trouvé</h3>
            <p className="text-gray-500">
              {selectedAnnee || selectedNiveau || selectedClasse || searchTerm
                ? 'Aucun bulletin ne correspond aux critères de recherche.'
                : 'Aucun bulletin n\'a été généré pour le moment.'
              }
            </p>
          </div>
        ) : (
          <>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {paginatedClasses.map((groupe, index) => (
                  <ClasseCard
                    key={groupe.classe?.id || index}
                    groupe={groupe}
                    onVoirBulletin={handleVoirBulletin}
                    onVoirTous={handleVoirTous}
                    onDownload={handleDownload}
                  />
                ))}
              </div>
            </div>

            {/* Pagination */}
            <Pagination
              pagination={paginationInfo}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              showPageSizeSelector={true}
              pageSizeOptions={[6, 12, 24, 48]}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default BulletinsTab; 